import { useMemo, ReactNode } from 'react';
import { darkModeAware } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import classnames from 'classnames';
import HelpText from '../HelpText/HelpText';
import Error, { IErrorProps } from '../Error/Error';
import LabelText, { ILabelTextProps } from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import SelectBase, { ISelectBaseProps } from './SelectBase';
import useStyles from './Select.styles';

export interface ISelectProps extends ISelectBaseProps, Omit<ILabelTextProps, 'label'> {
  /** Custom classes applied to label wrapping component */
  className?: string;
  /** Array of all current errors */
  errors?: IErrorProps['error'][];
  /** Help text to display with input */
  helpText?: ReactNode;
  labelSpanClass?: ILabelTextProps['className'];
  labelText?: ILabelTextProps['label'];
  /** Options for the select */
  options: { label: string; value: string }[];
  /** Placeholder text for the select */
  placeholderText?: string;
  /** boolean to indicate if select is required */
  required?: boolean;
  selectClass?: ISelectBaseProps['className'];
}

let uniqueId = 0;

const Select = ({
  className,
  dark,
  error,
  errors,
  helpText,
  htmlId,
  inline,
  labelSpanClass,
  labelText,
  optional,
  options,
  placeholderText,
  required,
  screenReaderLabel,
  selectClass,
  tooltipContent,
  warning,
  ...props
}: ISelectProps) => {
  const errorMessage = error || errors?.[0];
  const classes = useStyles();
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `select-${uniqueId++}`, [htmlId]);
  const errorId = useMemo(() => `error-${id}`, [id]);
  return (
    /** Note label-has-for was depreciated for label-has-associated-control
     * label-has-for doesn't support controlComponents config which lets you specify
     * that SelectBase is the control component for the label below. The rule has been disabled
     * for now
     */
    // eslint-disable-next-line jsx-a11y/label-has-for
    <label
      className={classnames(classes.root, className, { [classes.inline]: inline })}
      htmlFor={id}
    >
      <LabelText
        className={labelSpanClass}
        hasError={!!errorMessage}
        inline={inline}
        label={labelText}
        optional={optional}
        screenReaderLabel={screenReaderLabel}
        tooltipContent={tooltipContent}
        warning={warning}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      <div className={classnames(classes.wrap, { [classes.inlineSelect]: inline })}>
        <SelectBase
          aria-errormessage={errorMessage ? errorId : undefined}
          aria-invalid={!!errorMessage}
          className={selectClass}
          dark={dark}
          error={errorMessage}
          firstOption={
            placeholderText ? (
              <option disabled={required} value="">
                {placeholderText}
              </option>
            ) : null
          }
          htmlId={id}
          required={required}
          warning={warning}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        >
          {options.map((option) => {
            const { label, ...restOption } = option;
            return (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <option key={`${id}-${option.value}`} {...restOption}>
                {label}
              </option>
            );
          })}
        </SelectBase>

        <Error dark={dark} error={errorMessage} htmlId={errorId} warning={warning} />
        {helpText && <HelpText>{helpText}</HelpText>}
      </div>
      {optional && inline ? (
        <OptionalInlineText hasError={!!errorMessage} warning={warning} />
      ) : null}
    </label>
  );
};

const StyledSelect = darkModeAware(Select);
export { Select as BareSelect };
const WithInputSelect = withInputAPI(StyledSelect);
export default WithInputSelect;
export const ValidatedSelect = WithInputSelect;
